import React, { useMemo } from 'react';
import { PureQueryOptions } from '@apollo/client';
import { Employee } from '..';
import CategoriesAccordion from '../../../pages/Absences/CategoriesAccordion';
import { AbsencesContext } from '../../../pages/Absences/AbsencesContext';
import { EmployeeIdProvider } from '../../../context/EmployeeIdContext';
import { EmployeePageDocument } from '../../../__generated__/graphql';
import {
  addDateByUnit,
  changePeriodByUnit,
  formatDate,
} from '../../../../../utils/dateUtils';

type Props = {
  employee: Employee;
};

const currentPeriod = () => {
  const { start, end } = changePeriodByUnit(new Date(), { unit: 'year' });
  return {
    from: formatDate(start.setDate(start.getDate() - 1), 'yyyy-MM-dd'),
    to: formatDate(end, 'yyyy-MM-dd'),
  };
};

const timeSheetsPastWeeks = () => {
  const startingWeek = addDateByUnit(new Date(), -10, 'week');
  const endingWeek = addDateByUnit(new Date(), -1, 'week');
  const { start } = changePeriodByUnit(startingWeek, { unit: 'week' });
  const { end } = changePeriodByUnit(endingWeek, { unit: 'week' });

  return {
    timeSheetsFrom: formatDate(start, 'yyyy-MM-dd'),
    timeSheetsTo: formatDate(end, 'yyyy-MM-dd'),
  };
};

const AbsenceCategories: React.FC<Props> = ({ employee }) => {
  const categories = employee.absenceCategories.filter(
    (c) => !c.flair__Unlimited__c,
  );

  const refetchQueries = useMemo<PureQueryOptions[]>(
    () => [
      {
        query: EmployeePageDocument,
        variables: {
          id: employee.Id,
          ...currentPeriod(),
          ...timeSheetsPastWeeks(),
        },
      },
    ],
    [employee.Id],
  );

  return (
    <AbsencesContext.Provider
      value={{
        employeeId: employee.Id,
        timeSheets: employee.timeSheets,
      }}>
      <EmployeeIdProvider employeeId={employee.Id}>
        <CategoriesAccordion
          categories={categories}
          absencesCurrentYear={employee.absences}
          refetchQueries={refetchQueries}
        />
      </EmployeeIdProvider>
    </AbsencesContext.Provider>
  );
};

export default AbsenceCategories;
